/**
 * ShellProviders — the OUTER provider stack that wraps the router.
 *
 * SSOT for the `IntlProvider > ConfigProvider > FlagsProvider` layer that sits
 * ABOVE the router (the inner `shared/providers/AppProviders` — Query/Auth/… —
 * lives INSIDE the router via `root-layout`). Both the server (`render.tsx`,
 * wrapping `StaticRouterProvider`) and the client (`entry-client.tsx`, wrapping
 * `RouterProvider`) render this SAME stack, so the two hydration trees can only
 * differ at the router node — the React Router server/client contract — never in
 * the provider structure or the `defaultLocale`.
 */
import type { EvaluatedWebFlags } from '@letsgethome/lib/flags/evaluate';
import { DEFAULT_LOCALE } from '@letsgethome/types/branded';
import type { ReactNode } from 'react';
import { IntlProvider } from 'react-intl';

import { intlOnError } from '../shared/i18n/intl-error';
import { ConfigProvider, type RuntimeConfig } from '../shared/providers/ConfigProvider';
import { FlagsProvider } from '../shared/providers/FlagsProvider';

interface ShellProvidersProps {
  locale: string;
  messages: Record<string, string>;
  config: RuntimeConfig;
  flags: EvaluatedWebFlags;
  children: ReactNode;
}

// eslint-disable-next-line @letsgethome/no-passthrough-props -- provider-composition SSOT: this component EXISTS to thread the four request-scoped values into their respective context providers (Intl/Config/Flags) once, so the server render and client hydrate share one stack. Each value goes to a DISTINCT provider, not forwarded to a single child; consolidating them would just hide the composition.
export function ShellProviders({ locale, messages, config, flags, children }: ShellProvidersProps) {
  return (
    <IntlProvider
      locale={locale}
      messages={messages}
      defaultLocale={DEFAULT_LOCALE}
      onError={intlOnError}
    >
      <ConfigProvider config={config}>
        <FlagsProvider flags={flags}>{children}</FlagsProvider>
      </ConfigProvider>
    </IntlProvider>
  );
}
